import React, { useEffect, useState, useCallback } from 'react';
import { BasecampLayout } from '../../components/Layout';
import { PageHeader, Spinner, Alert, Btn, Badge, Input, Select, Table, SectionCard, Note } from '../../components/UI';
import { api, getUser } from '../../utils/api';

const isAdmin = (u) =>
  (u?.roles || []).includes('ADM') || (u?.roles || []).includes('SUPERADMIN') ||
  (u?.funcs || []).includes('System Administrator');

// "HB Way of Selling v1.1" already names its version — don't append the internal number.
const versionLabel = (p) => (/\bv\d/.test(p.name) ? p.name : `${p.name} v${p.version}`);

const ANCHOR_LABEL = { E: 'Explore', M: 'Master', D: 'Deliver' };

const emptyStage = { name: '', entryCriterion: '', anchor: '', primaryActionLabel: '', primaryActionRoute: '', defaultProbability: '' };
const toForm = (s) => ({
  name: s.name || '', entryCriterion: s.entryCriterion || '', anchor: s.anchor || '',
  primaryActionLabel: s.primaryActionLabel || '', primaryActionRoute: s.primaryActionRoute || '',
  defaultProbability: s.defaultProbability == null ? '' : String(s.defaultProbability),
});

// Inline form for adding/editing a stage (a "point" of HB Way of Selling).
function StageForm({ initial, onSave, onCancel, busy }) {
  const [form, setForm] = useState(initial);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const labelStyle = { fontSize: '0.8rem', fontWeight: 500, color: 'var(--ink-soft)', display: 'block', marginBottom: 5 };
  return (
    <div style={{ background: 'var(--canvas-warm)', borderRadius: 'var(--radius-md)', padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <label style={labelStyle}>Stage name *</label>
        <Input value={form.name} onChange={set('name')} placeholder="e.g. Needs Assessment" />
      </div>
      <div>
        <label style={labelStyle}>Entry criterion</label>
        <Input value={form.entryCriterion} onChange={set('entryCriterion')} placeholder="What must be true to enter this stage" />
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 120px' }}>
          <label style={labelStyle}>Phase anchor</label>
          <Select value={form.anchor} onChange={set('anchor')}>
            <option value="">— none —</option>
            <option value="E">Explore</option>
            <option value="M">Master</option>
            <option value="D">Deliver</option>
          </Select>
        </div>
        <div style={{ flex: '1 1 120px' }}>
          <label style={labelStyle}>Default probability (%)</label>
          <Input type="number" min="0" max="100" value={form.defaultProbability} onChange={set('defaultProbability')} placeholder="0–100" />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 160px' }}>
          <label style={labelStyle}>Primary action label</label>
          <Input value={form.primaryActionLabel} onChange={set('primaryActionLabel')} placeholder="e.g. Continue in Master → Proposals" />
        </div>
        <div style={{ flex: '1 1 160px' }}>
          <label style={labelStyle}>Primary action route</label>
          <Input value={form.primaryActionRoute} onChange={set('primaryActionRoute')} placeholder="/master/proposals" />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 2, justifyContent: 'flex-end' }}>
        <Btn size="sm" variant="ghost" onClick={onCancel} disabled={busy}>Cancel</Btn>
        <Btn size="sm" loading={busy} onClick={() => onSave(form)}>Save</Btn>
      </div>
    </div>
  );
}

export default function ProcessDesigner() {
  const user = getUser();
  const [process, setProcess] = useState(null);
  const [versions, setVersions] = useState([]);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);   // stageId being edited
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    Promise.all([api.getProcess(), api.getProcessVersions(), api.getProcessEvents()])
      .then(([p, v, e]) => { setProcess(p.process); setVersions(v.versions || []); setEvents(e.events || []); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!isAdmin(user)) {
    return (
      <BasecampLayout>
        <PageHeader title="Sales Process Designer" subtitle="Administration" />
        <Alert type="warn">This workplace is for the Administration function only.</Alert>
      </BasecampLayout>
    );
  }

  async function activate(id) {
    try { await api.activateProcess(id); load(); } catch (e) { alert(e.message); }
  }

  async function saveEdit(stageId, form) {
    setBusy(true);
    try { await api.updateProcessStage(stageId, form); setEditingId(null); load(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  async function saveNew(form) {
    setBusy(true);
    try { await api.addProcessStage(process.id, form); setAdding(false); load(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  async function removeStage(stage) {
    if (!window.confirm(`Delete stage "${stage.name}"? Its milestones and kit go with it. This cannot be undone.`)) return;
    setBusy(true);
    try { await api.deleteProcessStage(stage.id); load(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  async function move(index, dir) {
    const stages = process.stages;
    const target = index + dir;
    if (target < 0 || target >= stages.length) return;
    const order = stages.map(s => s.id);
    [order[index], order[target]] = [order[target], order[index]];
    setBusy(true);
    try { await api.reorderProcessStages(process.id, order); load(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }

  return (
    <BasecampLayout>
      <PageHeader title="Sales Process Designer" subtitle="The sales process as a governed, versioned record" />
      {error && <Alert type="error">{error}</Alert>}
      {loading ? <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner /></div> : (
        <>
          <Note>
            Edit the stages (the points of <b>HB Way of Selling</b>) here. Changes apply to the active process in place, so
            they show immediately in <b>My Pipeline → a customer</b> and in <b>My Pipeline → Funnel</b>. Activating another
            version leaves running projects on theirs.
          </Note>

          {/* Versions */}
          <SectionCard title="Versions">
            {versions.map((v, i) => (
              <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', fontSize: '0.9rem', borderTop: i ? '1px solid var(--line)' : 'none' }}>
                <b>{versionLabel(v)}</b>
                {v.isActive ? <Badge status="active">active</Badge> : <Badge status="default">inactive</Badge>}
                <span style={{ color: 'var(--ink-faint)', fontSize: '0.8rem' }}>{v.stages} stages · {v.projects} projects</span>
                {!v.isActive && <Btn size="sm" variant="outline" onClick={() => activate(v.id)} style={{ marginLeft: 'auto' }}>Activate</Btn>}
              </div>
            ))}
          </SectionCard>

          {/* Active process stages — editable */}
          {process && (
            <SectionCard title={`${versionLabel(process)} — stages`} action={
              <Btn size="sm" variant="outline" disabled={busy || adding}
                onClick={() => { setAdding(true); setEditingId(null); }}>+ Add stage</Btn>
            }>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {process.stages.map((s, i) => (
                  <div key={s.id} style={{ borderLeft: '3px solid var(--ink)', padding: '4px 0 4px 14px', marginBottom: 16 }}>
                    {editingId === s.id ? (
                      <StageForm initial={toForm(s)} busy={busy} onCancel={() => setEditingId(null)} onSave={(f) => saveEdit(s.id, f)} />
                    ) : (
                      <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <b style={{ fontSize: '0.95rem' }}>{s.seq}. {s.name}</b>
                          {s.anchor && <Badge status="default">{ANCHOR_LABEL[s.anchor] || s.anchor}</Badge>}
                          {s.defaultProbability != null && (
                            <span style={{ fontSize: '0.74rem', color: 'var(--ink-faint)' }}>{s.defaultProbability}% default</span>
                          )}
                          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 4 }}>
                            <Btn size="sm" variant="ghost" disabled={busy || i === 0} onClick={() => move(i, -1)} title="Move up">↑</Btn>
                            <Btn size="sm" variant="ghost" disabled={busy || i === process.stages.length - 1} onClick={() => move(i, 1)} title="Move down">↓</Btn>
                            <Btn size="sm" variant="outline" disabled={busy} onClick={() => { setEditingId(s.id); setAdding(false); }}>Edit</Btn>
                            <Btn size="sm" variant="ghost" disabled={busy} onClick={() => removeStage(s)} title="Delete stage" style={{ color: 'var(--danger)' }}>✕</Btn>
                          </span>
                        </div>
                        {s.entryCriterion && <div style={{ fontSize: '0.82rem', color: 'var(--ink-soft)', marginTop: 2 }}>Entry: {s.entryCriterion}</div>}
                        <div style={{ fontSize: '0.82rem', marginTop: 6 }}>
                          {s.milestones.map(m => (
                            <span key={m.id} style={{ display: 'inline-block', marginRight: 12, color: 'var(--ink-soft)' }}>
                              {m.kind === 'auto' ? '◆' : '●'} {m.label}
                            </span>
                          ))}
                        </div>
                        {s.kit.length > 0 && (
                          <div style={{ fontSize: '0.78rem', color: 'var(--ink-faint)', marginTop: 4 }}>
                            Kit: {s.kit.map(k => k.label).join(' · ')}
                          </div>
                        )}
                        {s.primaryActionLabel && (
                          <div style={{ fontSize: '0.78rem', color: 'var(--ink-faint)', marginTop: 2 }}>
                            Primary action: {s.primaryActionLabel} → <code>{s.primaryActionRoute}</code>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
                {adding && (
                  <div style={{ borderLeft: '3px solid var(--ink-faint)', paddingLeft: 14 }}>
                    <StageForm initial={emptyStage} busy={busy} onCancel={() => setAdding(false)} onSave={saveNew} />
                  </div>
                )}
              </div>
              <p style={{ fontSize: '0.76rem', color: 'var(--ink-faint)', marginTop: 12 }}>
                Milestones (● manual / ◆ auto) and kit are read-only here for now.
              </p>
              {process.lostReasons.length > 0 && (
                <div style={{ marginTop: 8, fontSize: '0.82rem', color: 'var(--ink-soft)' }}>
                  <b>Lost reasons:</b> {process.lostReasons.map(r => r.label).join(' · ')}
                </div>
              )}
            </SectionCard>
          )}

          {/* Auto-event catalogue */}
          {/* HB Way of Selling v1.1 — steps and confirmations (seeded by migration 147; read-only) */}
          {process && process.steps && process.steps.length > 0 && (
            <SectionCard title="Steps and confirmations" hint="The stage of a project is the highest stage reached by a confirmed step. Gate 1 and Gate 2 are confirmed per role track in Needs and Design.">
              <Table
                headers={['#', 'Step', 'What happens', 'Confirmation', 'Stage reached']}
                rows={process.steps.map(x => [
                  x.no, <b>{x.label}</b>,
                  <span style={{ fontSize: '0.8rem' }}>{x.does}</span>,
                  <span style={{ fontSize: '0.8rem' }}>{x.confirmation}</span>,
                  x.leadsTo == null ? '—' : `${x.leadsTo} · ${(process.stages.find(s => s.seq === x.leadsTo) || {}).name || ''}`,
                ])}
              />
            </SectionCard>
          )}

          <SectionCard title="Auto-event catalogue" hint="Auto milestones tick from these flow events. “Wired” means the triggering flow already fires it today.">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {events.map(e => (
                <span key={e.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', padding: '3px 9px', borderRadius: 99, border: '1px solid rgba(0,0,0,0.12)' }}>
                  <code>{e.key}</code>
                  <Badge status={e.wired ? 'active' : 'default'}>{e.wired ? 'wired' : 'pending'}</Badge>
                </span>
              ))}
            </div>
          </SectionCard>
        </>
      )}
    </BasecampLayout>
  );
}
